// Shared by StartTrialModal (existing teams with no plan) and SignupPage (brand-new signups) —
// both need to run the exact same trial-start motions, just from different entry points.

// TEMPORARY: fires the trial-start event to Zapier, which starts it on Chargebee's side —
// replace with a direct Chargebee call once that integration is ready, then drop this and the
// fetch() below. Not secret-bearing (a Zapier "catch hook" URL), safe to call from the browser.
const ZAPIER_TRIAL_START_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/7723146/4ycrcvg/'

export const TRIAL_LENGTH_DAYS = 30

type UpdateTeamMutation = (options: {
  variables: { changedTeam: Record<string, unknown> }
}) => Promise<{ data?: { updateTeam?: { returnCode?: number; message?: string } } }>

// Grants the trial in our own DB immediately via updateTeam so the admin can use the portal
// right away, independent of whether the Zapier -> Chargebee leg succeeds; see the TEMPORARY
// note above. Returns an error message on failure, or null on success.
export async function startTrial(
  updateTeam: UpdateTeamMutation,
  teamId: string,
  profileId: string | null | undefined,
  profileEmail: string | null | undefined,
): Promise<string | null> {
  const planExpiresAt = new Date(Date.now() + TRIAL_LENGTH_DAYS * 24 * 60 * 60 * 1000).toISOString()

  const { data } = await updateTeam({
    variables: {
      changedTeam: {
        id: teamId,
        planName: 'Trial',
        planExpiresAt,
        maxActiveTemplates: -1,
      },
    },
  })
  if (data?.updateTeam?.returnCode !== 0) {
    return data?.updateTeam?.message || 'Failed to start trial. Please try again.'
  }

  // Only fire once updateTeam has actually succeeded — best-effort from here (must not block
  // the trial the admin already has), but it shouldn't run at all on a failed update. See the
  // TEMPORARY note above for why this hop through Zapier exists at all.
  //
  // GET + query string, no custom headers — matches PushPage's proven pattern for the same
  // reason: a POST with a JSON/text Content-Type header forces a CORS preflight first, and
  // Zapier's catch-hook response doesn't allow Content-Type in Access-Control-Allow-Headers, so
  // the browser blocked the real request from ever being sent (confirmed: nothing reached
  // Zapier). A plain GET with no custom headers is always CORS-simple — no preflight needed.
  const webhookParams = new URLSearchParams({
    profileId: profileId ?? '',
    event: 'Trial Started',
    topLevelTeamId: teamId,
    profileEmailAddress: profileEmail ?? '',
  })
  fetch(`${ZAPIER_TRIAL_START_WEBHOOK_URL}?${webhookParams.toString()}`).catch(() => {})

  return null
}
